import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import Mapa from '../../components/Mapa';
import Historial from '../../components/Historial';
import './PanelGuardia.css';

const ANCHO_MAPA_BASE = 1120;
const ALTO_MAPA_BASE  = 620;

const PanelGuardia = () => {

  const [vista, setVista] = useState(() => {
      const vistaGuardada = localStorage.getItem('vistaPanelGuardia');
      return vistaGuardada ? vistaGuardada : 'mapa';
  });
  useEffect(() => {
      localStorage.setItem('vistaPanelGuardia', vista);
  }, [vista]);
  const [escala, setEscala]             = useState(1);
  const [mensaje, setMensaje]           = useState(null);
  const [datosMapa, setDatosMapa]       = useState(null);
  const [triggerGeneral, setTriggerGeneral] = useState(0);
  const [bloqueoFoco, setBloqueoFoco]   = useState(false);
  const [paso, setPaso]                 = useState(1);
  const [nombreTemporal, setNombreTemporal] = useState('');
  const [rutTemporal, setRutTemporal]   = useState('');
  const [inputValue, setInputValue]     = useState('');
  const [procesando, setProcesando]     = useState(false);
  const [modalVisita, setModalVisita]   = useState(false);
  const [lugarPendiente, setLugarPendiente] = useState(null);
  const [nombreVisita, setNombreVisita] = useState('');
  const [patenteVisita, setPatenteVisita] = useState('');
  const [zonaVisita, setZonaVisita]     = useState('');
  const [ultimosRegistros, setUltimosRegistros] = useState([]);

  const inputRef        = useRef(null);
  const inputNombreRef  = useRef(null);
  const rutActualRef = useRef('');
  const contenedorRef   = useRef(null);

  useEffect(() => {
    const calcularEscala = () => {
      if (!contenedorRef.current) return;
      const anchoDisponible = contenedorRef.current.offsetWidth - 20;
      setEscala(anchoDisponible < ANCHO_MAPA_BASE ? anchoDisponible / ANCHO_MAPA_BASE : 1);
    };
    const ro = new ResizeObserver(calcularEscala);
    if (contenedorRef.current) ro.observe(contenedorRef.current);
    calcularEscala();
    return () => ro.disconnect();
  }, []);

  const altoEscalado = Math.round(ALTO_MAPA_BASE * escala);

  const cargarUltimosRegistros = useCallback(async () => {
    try {
      const res = await api.get('/api/historial/');
      setUltimosRegistros(res.data.slice(0, 5));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    let timeoutId;
    const hacerPolling = async () => {
      await cargarUltimosRegistros();
      timeoutId = setTimeout(hacerPolling, 5000);
    };
    hacerPolling();
    return () => clearTimeout(timeoutId);
  }, [cargarUltimosRegistros]);

  useEffect(() => {
    if (vista !== 'mapa') return;
    const enfocar = () => {
      if (!bloqueoFoco && !modalVisita) inputRef.current?.focus();
      if (modalVisita) setTimeout(() => inputNombreRef.current?.focus(), 100);
    };
    enfocar();
    if (!bloqueoFoco && !modalVisita) window.addEventListener('click', enfocar);
    return () => window.removeEventListener('click', enfocar);
  }, [bloqueoFoco, mensaje, paso, modalVisita, vista]);

  useEffect(() => {
    if (mensaje?.tipo !== 'info') {
      const t = setTimeout(() => setMensaje(null), 4000);
      return () => clearTimeout(t);
    }
  }, [mensaje]);

  const enviarAlServidor = async (rut, id_lugar, datosVisita = null) => {
    setProcesando(true);
    try {
      const rutLimpio = rut ? rut.replace(/[^0-9kK]/g,'').toUpperCase() : null;
      const payload   = { rut:rutLimpio, id_lugar };
      if (datosVisita) {
        payload.nombre_visita = datosVisita.nombre;
        payload.patente       = datosVisita.patente;
        payload.zona          = datosVisita.zona;
      }
      const response = await api.post('/api/pistoleo/', payload);
      const datos    = response.data;
      setMensaje({
        tipo:    datos.tipo === 'entrada' ? 'exito-entrada' : 'exito-salida',
        texto:   datos.mensaje || "Operación Exitosa",
        detalle: datos.datos ? `LUGAR: ${datos.datos.lugar}` : ''
      });
      if (datos.datos) setDatosMapa(datos.datos);
      setTriggerGeneral(p => p + 1);
      cargarUltimosRegistros();
      setPaso(1); setRutTemporal(''); setNombreTemporal(''); rutActualRef.current = ''; setModalVisita(false);
      setNombreVisita(''); setPatenteVisita(''); setZonaVisita(''); setLugarPendiente(null);
    } catch (error) {
      const ed = error.response?.data;
      if (error.response?.status === 404 && ed?.codigo === 'RUT_NO_ENCONTRADO') {
        setLugarPendiente(id_lugar); setModalVisita(true); setMensaje(null);
      } else {
        setMensaje({ tipo:'error', texto:"ERROR", detalle: ed?.error || "Error de conexión" });
      }
    }
    setInputValue(''); setProcesando(false);
    if (!modalVisita) setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Al leer un RUT, consulta el nombre y lo muestra en lugar de "RUT LEÍDO"
  const mostrarNombre = async (rutLeido) => {
    try {
      const res = await api.get('/api/consultar-rut/', { params: { rut: rutLeido } });
      if (rutActualRef.current !== rutLeido) return;   // el guardia ya canceló o cambió de persona
      const sigueIgual = (prev) => prev && prev.tipo === 'info' && prev.rut === rutLeido;
      if (res.data.encontrado) {
        setNombreTemporal(res.data.nombre);
        setMensaje(prev => sigueIgual(prev)
          ? { ...prev, texto: res.data.nombre.toUpperCase(), detalle: `${res.data.cargo} · Escanee el LUGAR` }
          : prev);
      } else {
        setMensaje(prev => sigueIgual(prev)
          ? { ...prev, detalle: 'RUT no registrado (visita) · Escanee el LUGAR' }
          : prev);
      }
    } catch (e) { /* si la consulta falla se deja "RUT LEÍDO" y todo sigue igual */ }
  };

  const confirmarVisita = () => {
    if (!nombreVisita.trim()) return alert("Debe ingresar el nombre");
    enviarAlServidor(rutTemporal, lugarPendiente, { nombre:nombreVisita, patente:patenteVisita, zona:zonaVisita });
  };

  // Valida estructura de RUT chileno: 7-8 dígitos + verificador (0-9 o K)
  // Acepta con o sin puntos y con o sin guión (distintos formatos de escáner)
  const validarRut = (valor) => {
    const limpio = valor.replace(/\./g, '').replace(/-/g, '').toUpperCase();
    return /^\d{7,8}[0-9K]$/.test(limpio);
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || !inputValue) return;
    const v = inputValue.trim().toUpperCase();
    if (paso === 1) {
      // Si empieza con E. o es muy corto → código de lugar → salida directa
      if (v.startsWith("E.") || v.length <= 4) {
        enviarAlServidor(null, v);
      } else {
        // Es un RUT — validar estructura antes de aceptarlo
        if (!validarRut(v)) {
          setMensaje({ tipo:'error', texto:'RUT INVÁLIDO', detalle:'Formato: 12345678-9 o 12345678K' });
          setInputValue('');
          return;
        }
        const rutLeido = v.replace(/[^0-9kK]/g,'');
        setRutTemporal(rutLeido);
        setNombreTemporal('');
        rutActualRef.current = rutLeido;
        setInputValue('');
        setPaso(2);
        setMensaje({ tipo:'info', texto:'RUT LEÍDO', detalle:'Escanee el LUGAR', rut: rutLeido });
        mostrarNombre(rutLeido);
      }
    } else {
      enviarAlServidor(rutTemporal, v);
    }
  };

  const cancelarCiclo = () => {
    setPaso(1); setRutTemporal(''); setNombreTemporal(''); rutActualRef.current = ''; setInputValue(''); setMensaje(null); inputRef.current?.focus();
  };

  const extraerHora = (f) => (!f || f === "En curso") ? "--:--" : (f.split(' ')[1] || f);

  return (
    <div className="guardia-dashboard" style={{ background:'#f4f6f9', minHeight:'100vh', display:'flex', flexDirection:'column' }}>

      {/* ── BARRA SUPERIOR ── */}
      <div className={`barra-accion ${mensaje ? mensaje.tipo : ''}`} style={{ margin:'10px 20px', borderRadius:'8px', boxShadow:'0 2px 10px rgba(0,0,0,0.1)' }}>

        {/* Zona izquierda: logo + input */}
        <div className="input-zona">
          <img src="/DuocUC-1024x252.png" alt="Logo Duoc" style={{ height:'40px', marginRight:'8px', filter:'brightness(0) invert(1)' }} />
          {vista === 'mapa' && (
            <>
              <div style={{ display:'flex', flexDirection:'column', marginRight:'12px' }}>
                <span style={{ color:paso===2?'#FFB500':'white', fontWeight:'bold', fontSize:'1.1rem' }}>
                  {paso === 1 ? "ESCANEAR:" : "ASIGNAR A:"}
                </span>
                {paso === 2 && <span style={{ fontSize:'0.75rem', color:'#ccc', maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{nombreTemporal ? `${nombreTemporal} · ` : ''}RUT: {rutTemporal}</span>}
              </div>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value.replace(/[^0-9A-Za-zkK.-]/g,'').toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder={bloqueoFoco?"Pausado...":paso===1?"Esperando RUT o LUGAR...":"Esperando QR Lugar..."}
                disabled={procesando||bloqueoFoco||modalVisita}
                autoComplete="off"
                style={{
                  border:          paso===2 ? '3px solid #FFB500':'none',
                  backgroundColor: paso===2 ? '#fff8e1':'rgba(255,255,255,0.9)',
                  textTransform:   'uppercase',
                  textAlign:       'center',
                  fontSize:        '16px',
                  fontWeight:      'bold',
                  color:           '#000',
                  padding:         '9px',
                  borderRadius:    '4px',
                  width:           '280px',
                }}
              />
              {paso === 2 && (
                <button onClick={cancelarCiclo} style={{ marginLeft:'12px', background:'#dc3545', color:'white', border:'2px solid white', padding:'9px 16px', borderRadius:'4px', cursor:'pointer', fontWeight:'bold' }}>
                  CANCELAR
                </button>
              )}
            </>
          )}
        </div>

        {/* Zona central: notificación — justo al lado del input, sin marginLeft auto */}
        {mensaje && (
          <div className="notificacion-flotante">
            <span className="titulo-notif" style={{ display:'block', fontWeight:'bold', fontSize:'1.1rem' }}>{mensaje.texto}</span>
            <span className="detalle-notif" style={{ fontSize:'0.95rem' }}>{mensaje.detalle}</span>
          </div>
        )}

        {/* Zona derecha: botones de vista — empujados al extremo derecho con marginLeft auto
            solo los botones, no la notificación */}
        <div className="controles-vista" style={{ marginLeft:'auto' }}>
          <button className={`btn-toggle ${vista==='mapa'?'activo':''}`} onClick={() => setVista('mapa')}>MAPA OPERATIVO</button>
          <button className={`btn-toggle ${vista==='historial'?'activo':''}`} onClick={() => setVista('historial')}>HISTORIAL</button>
        </div>
      </div>

      {/* ── CONTENIDO ── */}
      <div className="contenido-dinamico" style={{ flex:1, display:'flex', flexDirection:'column' }}>
        {vista === 'mapa' ? (
          <div className="contenedor-dividido" style={{ display:'flex', flex:1, gap:'15px', padding:'0 16px 16px' }}>
            <div
              className="seccion-mapa"
              ref={contenedorRef}
              style={{
                flex:'0 0 72%', background:'white', borderRadius:'8px',
                boxShadow:'0 4px 6px rgba(0,0,0,0.05)', height:altoEscalado + 16,
                overflow:'hidden', display:'flex', justifyContent:'center',
                alignItems:'flex-start', padding:'8px',
              }}
            >
              <div style={{ transform:`scale(${escala})`, transformOrigin:'top center', transition:'transform 0.15s ease-out', width:ANCHO_MAPA_BASE, flexShrink:0 }}>
                <Mapa datosIngreso={datosMapa} recargar={triggerGeneral} alCambiarModal={setBloqueoFoco} />
              </div>
            </div>

            <div className="seccion-tabla" style={{ flex:1, background:'white', borderRadius:'8px', padding:'14px', boxShadow:'0 4px 6px rgba(0,0,0,0.05)', display:'flex', flexDirection:'column', minWidth:0 }}>
              <h3 style={{ marginTop:0, color:'#002D5C', borderBottom:'2px solid #FFB500', paddingBottom:'10px', fontSize:'1rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>Últimos Movimientos</span>
                <span style={{ fontSize:'0.75rem', background:'#e3f2fd', color:'#0d47a1', padding:'2px 6px', borderRadius:'10px' }}>En vivo</span>
              </h3>
              <div style={{ flex:1, overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                  <thead>
                    <tr style={{ color:'#666', textAlign:'center', borderBottom:'1px solid #eee' }}>
                      <th style={{ padding:'6px 4px' }}>Hora</th>
                      <th style={{ padding:'6px 4px' }}>Lugar</th>
                      <th style={{ padding:'6px 4px' }}>Patente</th>
                      <th style={{ padding:'6px 4px' }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ultimosRegistros.length === 0 ? (
                      <tr><td colSpan="4" style={{ padding:'20px', textAlign:'center', color:'#aaa' }}>Sin registros</td></tr>
                    ) : ultimosRegistros.map(mov => (
                      <tr key={mov.id} style={{ borderBottom:'1px solid #f8f9fa' }}>
                        <td style={{ padding:'8px 4px', fontWeight:'bold', color:'#333', fontSize:12 }}>
                          {mov.estado==='Activo' ? extraerHora(mov.ingreso) : extraerHora(mov.salida)}
                        </td>
                        <td style={{ padding:'8px 4px', color:'#002D5C', fontWeight:'bold', fontSize:12 }}>{mov.lugar}</td>
                        <td style={{ padding:'8px 4px', color:'#666', fontSize:12 }}>{mov.patente||'---'}</td>
                        <td style={{ padding:'8px 4px', textAlign:'center' }}>
                          {mov.estado==='Activo' ? <span style={tagEntrada}>ENTRA</span> : <span style={tagSalida}>SALE</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={() => setVista('historial')} style={{ marginTop:'10px', padding:'7px', background:'#f8f9fa', border:'1px solid #ccc', borderRadius:'4px', cursor:'pointer', color:'#002D5C', fontWeight:'bold', fontSize:'11px', width:'100%' }}>
                VER HISTORIAL COMPLETO
              </button>
            </div>
          </div>
        ) : (
          <div className="contenedor-historial-full">
            <Historial token={null} />
          </div>
        )}
      </div>

      {/* ── MODAL VISITA ── */}
      {modalVisita && (
        <div style={{ position:'fixed', top:0, left:0, width:'100vw', height:'100dvh', background:'rgba(0,0,0,0.85)', zIndex:5000, display:'flex', justifyContent:'center', alignItems:'flex-start', paddingTop:'max(50px, 8dvh)', padding:'max(50px, 8dvh) 16px 16px', boxSizing:'border-box', overflowY:'auto' }}>
          <div style={{ background:'white', padding:'28px', borderRadius:'15px', width:'100%', maxWidth:'400px', textAlign:'center', borderTop:'6px solid #FFB500', boxShadow:'0 0 30px rgba(255,181,0,0.3)', flexShrink:0 }}>
            <h2 style={{ color:'#002D5C', marginTop:0, fontSize:'1.3rem' }}>👤 NUEVA VISITA</h2>
            <p style={{ fontSize:'13px', color:'#666', marginBottom:'14px' }}>
              RUT: <strong>{rutTemporal}</strong> — Asignando a: <strong>{lugarPendiente}</strong>
            </p>
            <input ref={inputNombreRef} type="text" placeholder="Nombre Completo *" value={nombreVisita} onChange={e => setNombreVisita(e.target.value)} style={inputModal} maxLength={30} />
            <input type="text" placeholder="Patente" value={patenteVisita} onChange={e => setPatenteVisita(e.target.value.toUpperCase())} style={inputModal} maxLength={6} />
            <input type="text" placeholder="Dirección / Destino" value={zonaVisita} onChange={e => setZonaVisita(e.target.value)} style={inputModal} maxLength={30} />
            <div style={{ display:'flex', gap:'10px', marginTop:'12px' }}>
              <button onClick={() => { setModalVisita(false); cancelarCiclo(); }} style={btnCancel}>CANCELAR</button>
              <button onClick={confirmarVisita} style={btnConfirm}>REGISTRAR</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const tagEntrada = { background:'#d4edda', color:'#155724', padding:'2px 6px', borderRadius:'4px', fontSize:'10px', fontWeight:'bold', border:'1px solid #c3e6cb' };
const tagSalida  = { background:'#f8d7da', color:'#721c24', padding:'2px 6px', borderRadius:'4px', fontSize:'10px', fontWeight:'bold', border:'1px solid #f5c6cb' };
const inputModal = { width:'100%', padding:'10px', fontSize:'16px', border:'1px solid #ccc', borderRadius:'5px', marginBottom:'10px', boxSizing:'border-box' };
const btnCancel  = { flex:1, padding:'11px', background:'#ccc', border:'none', borderRadius:'5px', cursor:'pointer', fontWeight:'bold', color:'#333' };
const btnConfirm = { flex:1, padding:'11px', background:'#002D5C', border:'none', borderRadius:'5px', cursor:'pointer', fontWeight:'bold', color:'white' };

export default PanelGuardia;